import { useEffect, useState } from 'react';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NSTable, NSButton } from 'bricks';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import { numeralFormatter } from 'ns_libs/formatter';
import { IResidentialLotMix } from 'views/ProFormaTable/types';
import './ResidentialLotMixTable.scss';

export interface IResidentialLotMixTableProps {
    lotMixes: IResidentialLotMix[];
    onCreate: () => void;
    onUpdate: (lotMixId: number, dataKey: 'lotName' | 'lotWidth' | 'lotCount', value: string | number | null) => void;
    onDelete: (lotMixId: number) => void;
    onTotalsChange?: (totals: { totalLots: number; totalFrontFootage: number }) => void;
}

type LotMixFieldKey = 'lotName' | 'lotWidth' | 'lotCount';

const TH_CLASS = 'NSTable__thead__tr__th p-3 font-weight-normal align-middle text-white text-left';
const TD_CLASS = 'NSTable__tbody__tr__td p-3 align-middle text-left';
const TFOOT_TD_CLASS = 'NSTable__tfoot__tr__td p-3 align-middle text-left';
const DELETE_CELL_CLASS = 'py-3 px-2 align-middle text-center';
const DELETE_TH_CLASS = [
    'NSTable__thead__tr__th',
    'font-weight-normal',
    'align-middle',
    'text-white',
    DELETE_CELL_CLASS,
    'ResidentialLotMixTable__delete-col',
].join(' ');
const CELL_INPUT_HEIGHT = '32px';

const withComputedFrontFootage = (row: IResidentialLotMix): IResidentialLotMix => ({
    ...row,
    totalFrontFootage: (row.lotWidth || 0) * (row.lotCount || 0),
});

const ResidentialLotMixTable = ({
    lotMixes,
    onCreate,
    onUpdate,
    onDelete,
    onTotalsChange,
}: IResidentialLotMixTableProps) => {
    const [temporaryLotMixes, setTemporaryLotMixes] = useState<IResidentialLotMix[]>(lotMixes);

    const handleInputChange = (id: number, key: LotMixFieldKey, value: string | number | null) => {
        setTemporaryLotMixes(prev => prev.map(row => {
            if (row.id !== id) {
                return row;
            }
            const updatedRow = { ...row, [key]: value };
            return withComputedFrontFootage(updatedRow);
        }));
    };

    const commitChange = (id: number, key: LotMixFieldKey, value: string | number | null) => {
        handleInputChange(id, key, value);
        onUpdate(id, key, value);
    };

    useEffect(() => {
        setTemporaryLotMixes(lotMixes);
    }, [lotMixes]);

    const totalLots = temporaryLotMixes.reduce((sum, row) => sum + (row.lotCount || 0), 0);
    const totalFrontFootage = temporaryLotMixes.reduce((sum, row) => sum + (row.totalFrontFootage || 0), 0);

    useEffect(() => {
        onTotalsChange?.({ totalLots, totalFrontFootage });
    }, [totalLots, totalFrontFootage, onTotalsChange]);

    return (
        <>
            <div className="font-16 text-muted mb-2">Residential Lot Mix</div>
            <NSTable className="NSTable ResidentialLotMixTable mb-0">
                <thead className="NSTable__thead">
                    <tr className="NSTable__thead__tr">
                        <th className={`${TH_CLASS} ResidentialLotMixTable__col-lot-name`}>
                            <div className="d-flex align-items-center justify-content-between">
                                <span>Lot name</span>
                                <NSButton
                                    icon={faPlus}
                                    size="sm"
                                    className="px-2 text-muted hover:text-dark bg-none hover:bg-light"
                                    dataTestId="add-lot-mix-row"
                                    ariaLabel="Add lot mix row"
                                    callback={() => onCreate()}
                                />
                            </div>
                        </th>
                        <th className={`${TH_CLASS} ResidentialLotMixTable__col-lot-width`}>
                            Lot width (ft)
                        </th>
                        <th className={`${TH_CLASS} ResidentialLotMixTable__col-lot-count`}>
                            # of lots
                        </th>
                        <th className={`${TH_CLASS} ResidentialLotMixTable__col-front-footage`}>
                            Total front footage
                        </th>
                        <th className={DELETE_TH_CLASS} />
                    </tr>
                </thead>
                <tbody className="NSTable__tbody">
                    {temporaryLotMixes.map(row => (
                        <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                            <td className={`${TD_CLASS} ResidentialLotMixTable__col-lot-name`}>
                                <NSCellInput
                                    value={row.lotName ?? ''}
                                    handleUpdateValue={value => commitChange(row.id, 'lotName', String(value))}
                                    placeholder="Lot Group"
                                    inputHeight={CELL_INPUT_HEIGHT}
                                />
                            </td>
                            <td className={`${TD_CLASS} ResidentialLotMixTable__col-lot-width`}>
                                <NSCellInput
                                    type="number"
                                    value={row.lotWidth}
                                    handleUpdateValue={value => commitChange(row.id, 'lotWidth', Number(value))}
                                    placeholder="0"
                                    inputHeight={CELL_INPUT_HEIGHT}
                                />
                            </td>
                            <td className={`${TD_CLASS} ResidentialLotMixTable__col-lot-count`}>
                                <NSCellInput
                                    type="number"
                                    value={row.lotCount}
                                    handleUpdateValue={value => commitChange(row.id, 'lotCount', Number(value))}
                                    placeholder="0"
                                    inputHeight={CELL_INPUT_HEIGHT}
                                />
                            </td>
                            <td className={`${TD_CLASS} ResidentialLotMixTable__col-front-footage text-dark`}>
                                {numeralFormatter(row.totalFrontFootage, '0,0')}
                            </td>
                            <td className={`NSTable__tbody__tr__td ${DELETE_CELL_CLASS} ResidentialLotMixTable__delete-col`}>
                                <NSButton
                                    icon={faTrash}
                                    size="sm"
                                    className="px-2 text-muted hover:text-dark bg-none hover:bg-light"
                                    dataTestId={`delete-lot-mix-${row.id}`}
                                    ariaLabel={`Delete ${row.lotName || 'lot mix'} row`}
                                    callback={() => onDelete(row.id)}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className="NSTable__tfoot">
                    <tr className="NSTable__tfoot__tr">
                        <td className={`${TFOOT_TD_CLASS} ResidentialLotMixTable__col-lot-name text-dark font-weight-bold`}>
                            Total
                        </td>
                        <td className={`${TFOOT_TD_CLASS} ResidentialLotMixTable__col-lot-width`} />
                        <td className={`${TFOOT_TD_CLASS} ResidentialLotMixTable__col-lot-count text-dark font-weight-bold`}>
                            {numeralFormatter(totalLots, '0,0')}
                        </td>
                        <td className={`${TFOOT_TD_CLASS} ResidentialLotMixTable__col-front-footage text-dark font-weight-bold`}>
                            {numeralFormatter(totalFrontFootage, '0,0')}
                        </td>
                        <td className={`NSTable__tfoot__tr__td text-left ${DELETE_CELL_CLASS} ResidentialLotMixTable__delete-col`} />
                    </tr>
                </tfoot>
            </NSTable>
        </>
    );
};

export default ResidentialLotMixTable;
